import { circlesQuery } from "../../../services/queries";
import { viewQuery } from "../scopedQuery";
import { accessibleSubcircles } from "../../circles/circleAccess";
import { stripLeadIn } from "../leadInWords";
import { readTaskDetails } from "../taskDetails";
import { askFor, namedLoosely, findByName, findNamedIn, normal } from "./findByName";
import type { AnswerContext, SlotQuestion } from "./types";
import { noneCalled } from "./wording";

/** The Circles this person can see, as the Notices page's filter offers them. */
export async function loadCircles({ user, token, queryClient }: AnswerContext) {
  const circles = await queryClient.fetchQuery(viewQuery(user, circlesQuery(user?.schoolId, token)));
  return accessibleSubcircles(circles, user);
}

/** Words that mean "some Circle", so the question hasn't said which. */
const ANY_CIRCLE = /^(a|any|one|which|each|every|our|my)? ?(circle|class|group)s?$/;

/**
 * Words a question about a Circle asks with, never part of its name: "Who's
 * in Grade 3?" is asking about "Grade 3", not about "Who's in".
 */
const CIRCLE_QUESTION_WORDS = new Set([
  "who", "whos", "what", "whats", "which", "how", "many", "much", "is", "are", "in", "on", "of", "to", "for",
  "the", "a", "an", "this", "that", "it", "me", "we", "you", "our", "my", "does", "do", "have", "has",
  "list", "show", "see", "tell", "check", "belong", "belongs", "big", "size", "people",
  "member", "members", "circle", "circles", "class", "classes", "group", "groups",
]);

/** `said` with the words a question about a Circle asks with taken out, leaving just its name, if any. */
function withoutQuestionWords(said: string): string {
  return said
    .split(/\s+/)
    .filter((w) => !CIRCLE_QUESTION_WORDS.has(normal(w)))
    .join(" ");
}

/** The name a question gives for a Circle without its whole name: after "to" or "for", a short reply, or what's left once its own question words are taken out. */
function circleNameSaid(said: string): string | null {
  const audience = readTaskDetails(said).said.audience;
  if (audience) {
    const wanted = stripLeadIn(audience);
    return !wanted || ANY_CIRCLE.test(normal(wanted)) ? null : wanted;
  }
  const cleaned = stripLeadIn(said.replace(/[\s?.!]+$/, ""));
  if (!cleaned) return null;
  if (cleaned.split(/\s+/).length <= 3 && !ANY_CIRCLE.test(normal(cleaned))) return cleaned;
  const remainder = withoutQuestionWords(cleaned);
  return remainder && !ANY_CIRCLE.test(normal(remainder)) ? remainder : null;
}

/** What a Circle is named: found, needing a choice, or not named at all. */
export type CircleMatch<C> =
  | { circle: C }
  | { reason: "ambiguous"; names: string[] }
  | { reason: "notFound"; wanted: string }
  | { reason: "missing" };

/**
 * The Circle `said` names, by its whole name inside a longer sentence, or the
 * name it gives on its own, with lead-in words stripped first so "Actually,
 * Grade 3" and "Only for Grade 2" are read as "Grade 3" and "Grade 2".
 */
export function resolveCircle<C extends { name: string }>(circles: C[], said: string): CircleMatch<C> {
  const named = findNamedIn(circles, said, (c) => c.name);
  const wanted = named.length > 0 ? null : circleNameSaid(said);
  if (named.length === 0 && !wanted) return { reason: "missing" };
  let found = wanted ? findByName(circles, wanted, (c) => c.name) : named;
  // "my grade 4 class", "gr 4 parents", "the whole school": the name inside the everyday words around it
  if (found.length === 0 && wanted) found = namedLoosely(circles, wanted, (c) => c.name);
  if (found.length === 1) return { circle: found[0] };
  if (found.length > 1) return { reason: "ambiguous", names: found.map((c) => c.name) };
  return { reason: "notFound", wanted: wanted ?? said };
}

/**
 * `resolveCircle`'s result where a Circle is optional, as for Add Member and
 * Invite: ambiguous or notFound still ask, but "missing" (nothing real was
 * said, like "a circle") is simply left unset rather than asked again.
 */
export function askForNamedCircle<C>(
  resolved: CircleMatch<C>,
  question: string,
  names: string[]
): { circle?: C } | SlotQuestion {
  if ("circle" in resolved) return resolved;
  if (resolved.reason === "ambiguous") return askFor("audience", "ambiguous", question, resolved.names);
  if (resolved.reason === "notFound") return askFor("audience", "notFound", noneCalled("Circle", resolved.wanted, names), names);
  return {};
}

/** `resolveCircle`'s result, as the slot question a handoff or answer asks next, when a Circle is required. */
export function askForCircle<C>(
  resolved: CircleMatch<C>,
  question: string,
  names: string[]
): { circle: C } | SlotQuestion {
  if ("circle" in resolved) return resolved;
  if (resolved.reason === "missing") return askFor("audience", "missing", question, names);
  return askForNamedCircle(resolved, question, names) as SlotQuestion;
}

/** The Circle a question names: by its whole name, then by the name it gives. */
export function pickCircle<C extends { name: string }>(circles: C[], said: string) {
  const choices = circles.map((c) => c.name);
  const resolved = resolveCircle(circles, said);
  if ("circle" in resolved) return resolved;
  if (resolved.reason === "missing") {
    return askFor("circle", "missing", "Which Circle would you like to know about?", choices);
  }
  if (resolved.reason === "ambiguous") {
    return askFor("circle", "ambiguous", "Which Circle do you mean?", resolved.names);
  }
  return askFor("circle", "notFound", noneCalled("Circle", resolved.wanted, choices), choices);
}
